import { memo, useState } from "react";
import Spinner from "../../../globalComponents/ui/Spinner";
import AddKpisToAssetPanel from "./AddKpisToAssetPanel";
import { AssetEditRow, DeleteConfirmRow, IconBtn } from "./AssetRows";
import { assetScopeLabel } from "./helpers";
import { deleteAsset, deleteAssetsBulk, renameAsset, renameAssetsBulk } from "./kpiAssetApi";

/**
 * Asset row with expandable KPI list (inverse of KpiAssetRow).
 *
 * One row per distinct asset (title + scope + unit); expanding it lists every
 * KPI holding that asset. Members render straight from props — the page
 * refreshes them in the background after every mutation. This view owns the
 * cross-KPI flows: rename everywhere, remove from selected KPIs, delete
 * everywhere; single-KPI tweaks live in the By KPI view.
 */
function AssetKpiRow({
  group,
  allQuestions,
  sources,
  kpiAssetMap,
  selectedFYId,
  onSaved,
  disabled,
  isLast,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const [open, setOpen] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState(() => new Set());
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const { asset, members } = group;
  const { hasLocation, unitLabel } = assetScopeLabel(asset, sources);
  const isGlobal = !asset.isLocationRequired;

  /**
   * Selection pruned against current members, so ids removed by a background
   * refresh can't be re-deleted.
   */
  const selectedIds = members.filter(m => selectedMemberIds.has(m.id)).map(m => m.id);
  const showSelection = !disabled && canDelete && members.length > 1;

  /**
   * One delete flow: the trash targets the ticked KPIs when there is a
   * selection, every KPI otherwise.
   */
  const deleteTargetIds = selectedIds.length > 0 ? selectedIds : members.map(m => m.id);

  const toggleOpen = () => setOpen(o => !o);

  /**
   * Only toggles when the header itself is the target — key presses on the
   * buttons inside it must not collapse the row.
   */
  const onHeaderKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleOpen();
    }
  };

  const toggleMember = (id) => setSelectedMemberIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * Removes this asset from one KPI only; the row spinner stays up until the
   * background refresh lands so the row doesn't flash back untouched.
   */
  const handleDeleteMember = async (id) => {
    setDeletingMemberId(id);
    try {
      const res = await deleteAsset(id);
      if (res?.isSuccess !== false) await onSaved();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingMemberId(null);
    }
  };

  /**
   * Removes the asset from the given KPI mappings — the bulk endpoint skips
   * (and reports) mappings that reported data still references.
   */
  const handleBulkDelete = async (ids) => {
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const res = ids.length === 1
        ? await deleteAsset(ids[0])
        : await deleteAssetsBulk(ids);
      if (res?.isSuccess !== false) {
        setSelectedMemberIds(new Set());
        await onSaved();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBulkDeleting(false);
    }
  };

  const requestDelete = () => {
    if (members.length === 1) {
      handleBulkDelete([members[0].id]);
      return;
    }
    setEditing(false);
    setOpen(true);
    setConfirmDeleteAll(true);
  };

  /**
   * Rename applies to every KPI holding the asset (single-KPI renames live in
   * the By KPI view); title conflicts elsewhere are skipped and reported.
   */
  const handleEditSave = async (newTitle, extraIds) => {
    try {
      const res = extraIds.length
        ? await renameAssetsBulk([members[0].id, ...extraIds], newTitle)
        : await renameAsset(members[0].id, newTitle);
      if (res?.isSuccess !== false) {
        setEditing(false);
        await onSaved();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid var(--kpa-slate-100)" }}>
      <div
        className={`kpa-kpi-row-header${open ? " kpa-kpi-row-header--open" : ""}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={onHeaderKeyDown}
      >
        <div className={`kpa-caret${open ? " kpa-caret--open" : ""}`} aria-hidden="true">
          <span>▶</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="kpa-asset-title" title={asset.assetTitle}>{asset.assetTitle || "—"}</div>
          <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
            <span className={`kpa-tag ${hasLocation ? "kpa-tag--green" : "kpa-tag--amber"}`}>
              <i className={`fas ${hasLocation ? "fa-map-marker-alt" : "fa-globe"}`} style={{ marginRight: 3, fontSize: 9 }} aria-hidden="true" />
              {isGlobal ? "Global" : unitLabel}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span className={`kpa-count-pill ${isGlobal ? "kpa-count-pill--amber" : "kpa-count-pill--blue"}`}>
            {members.length} KPI{members.length !== 1 ? "s" : ""}
          </span>
          {bulkDeleting ? (
            <Spinner size={14} color="#ef4444" />
          ) : (
            <>
              {!disabled && canCreate && (
                <button
                  type="button"
                  className="kpa-row-add-btn"
                  onClick={e => { e.stopPropagation(); setOpen(true); setShowAddPanel(true); }}
                >
                  <i className="fas fa-plus" style={{ fontSize: 9 }} aria-hidden="true" /> Add KPIs
                </button>
              )}
              {!disabled && canUpdate && (
                <IconBtn
                  icon="fa-pen"
                  variant="edit"
                  label={`Rename ${asset.assetTitle}`}
                  onClick={() => { setConfirmDeleteAll(false); setOpen(true); setEditing(true); }}
                />
              )}
              {!disabled && canDelete && (
                <IconBtn
                  icon="fa-trash"
                  variant="delete"
                  label={selectedIds.length > 0
                    ? `Delete ${asset.assetTitle} from the selected KPIs`
                    : `Delete ${asset.assetTitle}`}
                  onClick={requestDelete}
                />
              )}
            </>
          )}
        </div>
      </div>

      {open && (
        <div style={{ background: "var(--kpa-slate-50)", borderTop: "1px solid var(--kpa-slate-100)" }}>
          {showAddPanel && !disabled && canCreate && (
            <AddKpisToAssetPanel
              group={group}
              allQuestions={allQuestions}
              kpiAssetMap={kpiAssetMap}
              sources={sources}
              selectedFYId={selectedFYId}
              onSaved={onSaved}
              onClose={() => setShowAddPanel(false)}
            />
          )}

          <div className="kpa-inner-list">
            {editing && (
              <AssetEditRow
                asset={members[0].asset}
                sources={sources}
                allAssets={kpiAssetMap[members[0].kpiId] || []}
                crossKpiMatches={members.slice(1).map(m => ({
                  id: m.id,
                  kpiId: m.kpiId,
                  kpiTitle: m.kpiTitle,
                  assets: kpiAssetMap[m.kpiId] || [],
                }))}
                onSave={handleEditSave}
                onCancel={() => setEditing(false)}
              />
            )}
            {confirmDeleteAll && (
              <DeleteConfirmRow
                message={selectedIds.length > 0
                  ? `Delete “${asset.assetTitle}” from the ${selectedIds.length} selected KPI${selectedIds.length !== 1 ? "s" : ""}?`
                  : `Delete “${asset.assetTitle}” from all ${members.length} KPIs?`}
                confirmLabel={`Delete from ${deleteTargetIds.length} KPI${deleteTargetIds.length !== 1 ? "s" : ""}`}
                onCancel={() => setConfirmDeleteAll(false)}
                onConfirm={() => { setConfirmDeleteAll(false); handleBulkDelete(deleteTargetIds); }}
              />
            )}

            <div className="kpa-inner-list-header">
              <span className="kpa-list-header-label">KPIs</span>
              <span style={{ fontSize: 10, color: "var(--kpa-slate-400)" }}>
                {showSelection && selectedIds.length > 0 ? `${selectedIds.length} selected · ` : ""}
                {members.length} item{members.length !== 1 ? "s" : ""}
              </span>
            </div>
            {members.map(member => (
              <div key={member.id} className={`kpa-asset-row${deletingMemberId === member.id ? " kpa-asset-row--deleting" : ""}`}>
                {showSelection && (
                  <input
                    type="checkbox"
                    className="kpa-checkbox"
                    style={{ width: 14, height: 14, flexShrink: 0 }}
                    checked={selectedMemberIds.has(member.id)}
                    disabled={bulkDeleting || deletingMemberId === member.id}
                    onChange={() => toggleMember(member.id)}
                    aria-label={`Select ${member.kpiTitle || "KPI"} for removal`}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className={`kpa-kpi-title${deletingMemberId === member.id ? " kpa-asset-title--deleting" : ""}`}
                    title={member.kpiTitle}
                  >
                    {member.kpiTitle || "—"}
                  </div>
                </div>
                {deletingMemberId === member.id ? (
                  <Spinner size={14} color="#ef4444" />
                ) : (
                  !disabled && canDelete && members.length > 1 && (
                    <IconBtn
                      icon="fa-trash"
                      variant="delete"
                      label={`Remove ${asset.assetTitle} from ${member.kpiTitle || "this KPI"}`}
                      onClick={() => handleDeleteMember(member.id)}
                    />
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Memoized: page-level search/filter keystrokes re-render the list, but rows
 * with unchanged props (stable group objects + stable callbacks) skip their
 * subtree.
 */
export default memo(AssetKpiRow);
